import { isPublicId, isUuid, type Scope, type ScopeList } from '@hlt/contracts';
import { onlineManager, type QueryClient } from '@tanstack/react-query';
import { notFound, redirect, type ParsedLocation } from '@tanstack/react-router';
import { ApiError, apiQueryOptions, idOfPublicId, isApiError, publicIdOf, rememberPublicIds } from '@hlt/web-kit/api';

/**
 * Short 5S addresses: `/w/<workspace>/audits/<audit>` with public identifiers.
 * Inside the app every route parameter is the UUID, so pages, query keys, offline drafts and realtime events
 * are unchanged. `parse` maps a known public identifier to its UUID and `stringify` writes the public
 * identifier of a UUID into links (both from the identifiers the API returned, see `rememberPublicIds`).
 * A route resolves a value the browser has not seen yet (`resolveWorkspace`, `resolveResource`) and then
 * opens its canonical address once.
 */
export function publicIdParam<Name extends string>(name: Name) {
    return {
        parse: (raw: Record<Name, string>): Record<Name, string> =>
            ({ [name]: idOfPublicId(raw[name]) ?? raw[name] }) as Record<Name, string>,
        stringify: (params: Record<Name, string>): Record<Name, string> =>
            ({ [name]: publicIdOf(params[name]) ?? params[name] }) as Record<Name, string>,
    };
}

export interface WorkspaceRouteScope {
    organizationId: string;
    workspaceId: string;
}

const scopesQuery = (input: { limit: number; search?: string }) => ({
    ...apiQueryOptions('listScopes', { input }),
    staleTime: 60_000,
});

/** The address with one identifier segment (after `/<collection>/`) replaced. */
function withSegment(href: string, collection: string, value: string): string {
    return href.replace(new RegExp(`/${collection}/[^/?#]+`), `/${collection}/${value}`);
}

/** The identifier segment after `/<collection>/` in a path. */
const segmentOf = (pathname: string, collection: string): string | undefined =>
    new RegExp(`/${collection}/([^/]+)`).exec(pathname)?.[1];

async function findScope(queryClient: QueryClient, ref: string): Promise<Scope | null> {
    const cached = queryClient.getQueryData<ScopeList>(scopesQuery({ limit: 100 }).queryKey);
    if (!cached && !onlineManager.isOnline())
        throw new ApiError(0, 'network', 'You appear to be offline.', [], null, true);
    const scopes: ScopeList = cached ?? (await queryClient.fetchQuery(scopesQuery({ limit: 100 })));
    rememberPublicIds(scopes);
    const match = (s: Scope) => s.workspaceId === ref || s.workspacePublicId === ref;
    const found = scopes.items.find(match);
    if (found || scopes.items.length < 100) return found ?? null;
    // More workspaces than one page: look the address up directly (the search matches identifiers too).
    const searched: ScopeList = await queryClient.fetchQuery(scopesQuery({ limit: 100, search: ref }));
    rememberPublicIds(searched);
    return searched.items.find(match) ?? null;
}

/**
 * The organization and UUID of the workspace in the address, from the workspaces this person may enter.
 * The address names only the workspace; the API still receives organization and workspace and checks the
 * membership (an unknown or foreign workspace is the same "not found" as before). Older addresses (UUID,
 * 22-character form) open the canonical short address.
 */
export async function resolveWorkspace(
    queryClient: QueryClient,
    ref: string,
    location: ParsedLocation,
): Promise<WorkspaceRouteScope> {
    const scope = await findScope(queryClient, ref);
    if (!scope) throw notFound();
    if (segmentOf(location.pathname, 'w') !== scope.workspacePublicId)
        throw redirect({ href: withSegment(location.href, 'w', scope.workspacePublicId), replace: true });
    return { organizationId: scope.organizationId, workspaceId: scope.workspaceId };
}

/**
 * A workspace resource in the address (audit, finding, template, dashboard): a public identifier the browser
 * has not seen is read once through the API (which resolves it inside the authorized workspace) and the page
 * opens again with its UUID; an older UUID address opens the short one when it is known.
 */
export async function resolveResource(options: {
    ref: string;
    collection: 'audits' | 'findings' | 'templates' | 'analytics';
    location: ParsedLocation;
    read: () => Promise<unknown>;
}): Promise<void> {
    const { ref, collection, location } = options;
    if (!isUuid(ref)) {
        if (!isPublicId(ref)) throw notFound();
        try {
            await options.read();
        } catch (error) {
            if (isApiError(error) && (error.code === 'not_found' || error.status === 404)) throw notFound();
            throw error;
        }
        if (!idOfPublicId(ref)) throw notFound();
        throw redirect({ href: location.href, replace: true });
    }
    const publicId = publicIdOf(ref);
    if (publicId && segmentOf(location.pathname, collection) !== publicId)
        throw redirect({ href: withSegment(location.href, collection, publicId), replace: true });
}
